// CumulativeRevenueChart.tsx

import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/system';
import moment from 'moment';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { MdBarChart } from 'react-icons/md';
import Card from '../../../components/card/Card';
import { translate } from '../../../i18n';
import { useChartOptions } from '../../../hooks/useChartOptions';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { formatDemoMonth, getDemoAnnotations, getDemoCumulativeRevenue, getDemoXAxisMax, getDemoXAxisMin, isDemoOrganization } from '../demoAnalyticsData';
import { AnalyticsFilterParams } from '../hooks/analyticsFilterParams';
import { useGetCumulativeRevenue } from '../hooks/useGetCumulativeRevenue';
import { classifyRead } from '../../_shared/readState';
import ChartReadState, { chartHasSomethingToDraw } from './ChartReadState';

const CumulativeRevenueChart = (props: { filters?: AnalyticsFilterParams; startDate?: Date; endDate?: Date }) => {
    const { startDate, endDate, filters } = props;
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const orgId = selectedOrganization?.id;
    const isDemo = isDemoOrganization(orgId);
    const { data: realData, isInitialLoading, isError, refetch } = useGetCumulativeRevenue(selectedLocation?.id, startDate, endDate, filters);
    const lineChartData = useMemo(() => isDemo ? getDemoCumulativeRevenue(orgId) : realData, [isDemo, realData, orgId]);
    /**
     * A refused read leaves `realData` undefined, and so does a read that was
     * never ISSUED — this query is `enabled`-gated, and React Query v4 reports
     * a disabled query as `isLoading: true` with `fetchStatus: 'idle'` forever.
     * Run 99 separated the refused case from the chart; the never-issued case
     * still fell through to it and drew the same empty series, permanently.
     * Both are now classified once, by the shared 4-state classifier.
     *
     * `classifyRead` reads the query's OWN flags instead of asking this file to
     * restate the hook's `enabled:` expression, which is the correction D62
     * asked for and which the predicate this replaced required.
     */
    const readState = isDemo ? 'answered' : classifyRead(isError, isInitialLoading, realData);

    const seriesData: ApexAxisChartSeries | ApexNonAxisChartSeries = useMemo(() => {
        return [
            {
                name: translate('analytics.revenue'), data: (lineChartData || []).map(d => {
                    return [new Date(d.date).getTime(), d.amount]
                })
            }];
    }, [lineChartData]);

    const baseChart = useChartOptions();
    const options: ApexCharts.ApexOptions = {
        ...baseChart,
        xaxis: {
            tickAmount: 5,
            ...(isDemo ? { min: getDemoXAxisMin(orgId), max: getDemoXAxisMax(orgId) } : {}),
            labels: {
                showDuplicates: false,
                formatter: (val) => {
                    return isDemo ? formatDemoMonth(val, orgId) : moment(val).format("MMM DD");
                }
            }

        },
        tooltip: {
            ...baseChart.tooltip,
            x: {
                formatter: (val) => {
                    return isDemo ? formatDemoMonth(val, orgId) : moment(val).format("MMM DD YY");
                },
            },
            y: {
                formatter: (val) => {
                    return val != null ? `${val.toFixed(2)}` : '0';
                },
            },
        },
        yaxis: {
            ...(isDemo ? { min: 15000 } : {}),
            labels: {
                formatter: (val) => {
                    return val != null ? val.toFixed(0) : '0';
                },
            },
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.3,
                opacityTo: 0.01,
                stops: [20, 100, 100, 100]
            },
        },
        annotations: isDemo ? getDemoAnnotations(orgId) : { xaxis: [] },
    };

    // Chakra Color Mode
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const iconColor = useColorModeValue('brand.500', 'white');
    const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
    const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

    return (
        <Card alignItems='center' flexDirection='column' w='100%'>
            <Flex align='center' w='100%' px='15px' py='10px'>
                <Text me='auto' color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    {translate('analytics.revenue')}
                </Text>
                {Boolean(!lineChartData && readState === 'loading') && <Button
                    isLoading
                    alignItems='center'
                    justifyContent='center'
                    bg={bgButton}
                    _hover={bgHover}
                    _focus={bgFocus}
                    _active={bgFocus}
                    w='37px'
                    h='37px'
                    lineHeight='100%'
                    borderRadius='10px'
>
                    <Icon as={MdBarChart} color={iconColor} w='24px' h='24px' />
                </Button>}
            </Flex>

            <Box h='350px' mt='auto' w='100%'>
                {!chartHasSomethingToDraw(readState) ? (
                    <ChartReadState state={readState} gate='location' onRetry={() => { refetch(); }} />
                ) : (
                    <ReactApexChart
                        key={`revenue-${isDemo}`}
                        options={options}
                        series={seriesData}
                        type="area"
                        height={350}
                    />
                )}
            </Box>
        </Card>
    );
};

export default CumulativeRevenueChart;
